import { useEffect, useRef, useState } from 'react'

import { useLiveChatSDK } from '@inno-sdk/livechat-sdk'
import { ChevronRightIcon, IconSizes, MultiplyIcon } from '@inno-ui/icons'
import cn from 'classnames'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import {
  AppModals,
  LogViewer,
  PreferencesWindow,
  TrialExpiryModal,
  UsercentricsQuickAction,
  WallpapersSelector
} from 'modules/desktop/components'
import { handleCustomShortcutDeepLink } from 'modules/desktop/modules/files-integration/utils'
import { SttQuickAction } from 'modules/desktop/modules/stt'
import {
  DesktopContextMenuProvider,
  DesktopGrid,
  SidebarWidgets,
  WindowManager
} from 'modules/desktop/pages/MainPage/components'
import { motion } from 'motion/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppLocationType } from 'shared/components/MainLayout/components/LaunchPad/LaunchPad.types'
import { appConfigs } from 'shared/configs/appConfigs'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { useMediaQuery } from 'shared/hooks/useMediaQuery'
import { logger } from 'shared/services/logger'
import { useCommonStore } from 'shared/stores/commonStore'
import { usePreferencesStore } from 'shared/stores/preferencesStore'
import { AppInstance } from 'shared/types'
import { SnapArea } from 'shared/types'
import { MainLayout } from 'src/shared/components'
import { useTopDockHeight } from 'src/shared/components/MainLayout/hooks/useTopDockHeight'

import styles from './MainPage.module.scss'
import {
  calculateSnapRect,
  convertSnapRectWidthToNumber
} from './components/WindowManager/components/Window/utils'
import { useIsWindowMarketplaceOpen } from './hooks/useIsWindowMarketplaceOpen'
import { useSidebarWidgetsStore, useWindowStore } from './stores'
import { useSetWindowPositions } from './utils/useSetWindowPositions'
import { useSetWindowSizes } from './utils/useSetWindowSizes'

export const MainPage = () => {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { livechat } = useLiveChatSDK()

  const { apps: availableApps } = useAvailableApps()

  const isWidgetMarketplaceOpen = useIsWindowMarketplaceOpen()
  const widgetsPlacement = useSidebarWidgetsStore(state => state.placement)

  const showDarkOverlay = isWidgetMarketplaceOpen

  const [isWidgetsSidebarOpen, setIsWidgetsSidebarOpen] = useState(false)

  const contentAreaRef = useRef<HTMLDivElement>(null)
  const topDockIconRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const launchpadIconRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const { height: topDockHeight, topDockRef } = useTopDockHeight()
  const { isMobile } = useMediaQuery()

  const openWindow = useWindowStore(state => state.openWindow)
  const getWindowState = useWindowStore(state => state.getWindowState)
  const minimizeWindow = useWindowStore(state => state.minimizeWindow)
  const activeWindowId = useWindowStore(state => state.activeWindowId)
  const setActiveWindow = useWindowStore(state => state.setActiveWindow)
  const setWindowMaximized = useWindowStore(state => state.setWindowMaximized)
  const windows = useWindowStore(state => state.windows)
  const storedAppOpeningMode = usePreferencesStore(
    state => state.defaultAppOpeningMode
  )
  const defaultAppOpeningMode = FEATURE_FLAGS.FORCE_DETACHED_MODE
    ? 'detached'
    : storedAppOpeningMode

  const setIsDetached = useCommonStore(state => state.setIsDetached)
  const updateWindow = useWindowStore(state => state.updateWindow)

  useSetWindowSizes()
  useSetWindowPositions()

  useEffect(() => {
    setIsDetached(false)
  }, [setIsDetached])

  useEffect(() => {
    handleCustomShortcutDeepLink(search, { livechat })
  }, [search, livechat])

  const openAtIcon = (
    app: AppInstance,
    iconElement?: HTMLDivElement | null
  ) => {
    if (defaultAppOpeningMode === 'detached' && app.url) {
      const appOpenLogPayload = {
        windowId: app.id,
        appName: app.label,
        openMode: defaultAppOpeningMode,
        type: 'iframe',
        url: app.url,
        openedFromDock: !!iconElement
      }

      logger.track('detached_app.opened', appOpenLogPayload)
      navigate(app.url)
      return
    }

    const existingWindow = getWindowState(app.id)

    if (
      existingWindow?.snapArea &&
      existingWindow.snapArea !== SnapArea.FULLSCREEN
    ) {
      const snapRect = calculateSnapRect(existingWindow.snapArea, topDockHeight)
      const snapWidth = convertSnapRectWidthToNumber(snapRect.width)
      const snapHeight = snapRect.height as number
      updateWindow(app.id, {
        position: { x: snapRect.x, y: snapRect.y },
        size: { width: snapWidth, height: snapHeight },
        snapArea: existingWindow.snapArea,
        isVisible: true,
        isMinimized: false
      })
      setActiveWindow(app.id)
      return
    }

    const appOpenLogPayload = {
      windowId: app.id,
      appName: app.label,
      openMode: defaultAppOpeningMode,
      type: app.url ? 'iframe' : 'local',
      url: app.url ?? null,
      openedFromDock: !!iconElement
    }

    logger.track('window.opened', appOpenLogPayload)

    if (iconElement) {
      const rect = iconElement.getBoundingClientRect()
      const dockIconPosition = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      }
      openWindow(app, dockIconPosition)
    } else {
      openWindow(app)
    }

    if (defaultAppOpeningMode === 'maximized') {
      setWindowMaximized(app.id, true)
    }
  }

  const handleAppClick = (
    app: AppInstance,
    type: AppLocationType | 'universalSearch'
  ) => {
    if (isMobile && app?.url) {
      navigate(app.url)
      return
    }
    if (
      Object.values(availableApps).some(
        availableApp => availableApp.id === app.id && availableApp.isDisabled
      )
    ) {
      return
    }

    switch (type) {
      case 'topDock': {
        const existingWindow = getWindowState(app.id)
        if (!existingWindow || existingWindow.isMinimized) {
          openAtIcon(app, topDockIconRefs.current[app.id])
        } else {
          if (activeWindowId === app.id) {
            minimizeWindow(app.id)
            const nonMinimizedWindows = windows.filter(
              w => !w.isMinimized && w.id !== app.id
            )
            if (nonMinimizedWindows.length > 0) {
              setActiveWindow(nonMinimizedWindows[0].id)
            }
          } else {
            setActiveWindow(app.id)
          }
        }
        break
      }

      case 'launchpad': {
        openAtIcon(app, launchpadIconRefs.current[app.id])
        break
      }

      default: {
        openAtIcon(app)
        break
      }
    }
  }

  return (
    <MainLayout
      topDockIconRefs={topDockIconRefs}
      launchpadIconRefs={launchpadIconRefs}
      contentAreaRef={contentAreaRef}
      handleAppClick={handleAppClick}
      topDockHeight={topDockHeight}
      topDockRef={topDockRef}
      quickActions={
        <>
          <SttQuickAction />
          <UsercentricsQuickAction />
        </>
      }
    >
      <TrialExpiryModal />
      <WindowManager
        constraintsRef={contentAreaRef}
        topDockHeight={topDockHeight}
      />
      <WallpapersSelector />
      <PreferencesWindow />
      <LogViewer />

      {Object.entries(appConfigs).map(([id, config]) => {
        return config?.modals ? (
          <AppModals key={`${id}-modals`} appConfig={config} />
        ) : null
      })}

      <DesktopContextMenuProvider>
        <div
          className={cn(styles.mainContent, {
            [styles.widgetsPlacementLeft]: widgetsPlacement === 'left',
            [styles.widgetsPlacementRight]: widgetsPlacement === 'right'
          })}
        >
          <div
            className={cn(styles.darkOverlay, {
              [styles.visible]: showDarkOverlay
            })}
          />

          <motion.aside
            className={cn(styles.leftSidebar, {
              [styles.open]: isWidgetsSidebarOpen
            })}
          >
            <SidebarWidgets
              showDropBackground={isWidgetMarketplaceOpen}
              showRemoveWidgetButtons={isWidgetMarketplaceOpen}
            />

            <button
              className={styles.widgetsSidebarToggler}
              onClick={() => setIsWidgetsSidebarOpen(v => !v)}
            >
              <ChevronRightIcon
                size={IconSizes.Size16}
                color='var(--white)'
                className={styles.icon}
              />
            </button>

            <button
              className={styles.closeSidebarButton}
              onClick={() => setIsWidgetsSidebarOpen(false)}
            >
              <MultiplyIcon size={IconSizes.Size24} color='var(--white)' />
            </button>
          </motion.aside>

          <div className={styles.desktopArea}>
            <DesktopGrid />
          </div>
        </div>
      </DesktopContextMenuProvider>
    </MainLayout>
  )
}
